import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import {
  useGetSingleHierarchyQuery,
  useGetActiveStaffQuery,
} from "../../features/employeeSlice";
import { useGetAllProjectsQuery } from "../../features/projectSlice";
import { useLazyGetTsActivityReportQuery } from "../../features/tsActivitySlice";
import { colors } from "../../Shared/colors";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { TsKpiDisplay } from "../Reports/TsKpiDisplay";
import CryptoJS from "crypto-js";
import { decryptData } from "../../services/crypto";

export const TsReportFirst = () => {
  let staffId = null;
  let storedStaffId = localStorage.getItem("id");

  if (storedStaffId) {
    try {
      staffId = decryptData(storedStaffId);
    } catch (error) {
      console.error("Failed to decrypt data:", error);
    }
  }

  let user = null;
  let role = null;
  let storedStaffRole = localStorage.getItem("role");

  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      console.error("Failed to decrypt data:", error);
    }
  }

  const [maxDate, setmaxDate] = useState("");
  const [minDate, setminDate] = useState("");

  const today = new Date();
  const numberOfDaysToAdd = 0;
  const date = today.setDate(today.getDate() + numberOfDaysToAdd);
  const defaultDate = new Date(date).toISOString().split("T")[0];

  useEffect(() => {
    setminDate(defaultDate);
    setmaxDate(defaultDate);
  }, [defaultDate]);

  const { data: hierarchyStaff } = useGetSingleHierarchyQuery({ staffId });
  const { data: staff } = useGetActiveStaffQuery();
  const { data: project } = useGetAllProjectsQuery();

  const [kpiData, setkpiData] = useState({
    start: defaultDate,
    end: defaultDate,
    staff: [],
    project: "",
  });

  // TS Activity API hook
  const [triggerTsActivity, { data: tsActivityData, isFetching }] = useLazyGetTsActivityReportQuery();

  // Extract data from API response
  const tsKpiData = tsActivityData?.data || [];
  const leadsWorking = tsActivityData?.leadsWorking || [];
  const overdueLeads = tsActivityData?.overdueLeads || [];
  const overdueTasksByType = tsActivityData?.overdueTasksByType || {};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setkpiData({
      ...kpiData,
      [name]: value,
    });
  };

  const handleSearch = () => {
    // Prepare staff IDs array
    const staffIds = kpiData.staff?.map((staff) => staff.id) || [];
    
    // Call the API
    triggerTsActivity({
      start: kpiData.start,
      end: kpiData.end,
      staff: staffIds.length > 0 ? staffIds : undefined,
      project: kpiData.project || undefined,
    });
  };

  const options = useMemo(() => {
    let options = [];
    if (hierarchyStaff?.length > 0) {
      // Filter to only show employees with role = "ts" (telesales agents)
      hierarchyStaff
        ?.filter((staff) => staff?.role?.toLowerCase() === "ts")
        ?.forEach((staff) =>
          options.push({
            id: staff?._id,
            label: `${staff?.firstName} ${staff?.lastName}`,
          })
        );
    } else {
      // Only add user if they have ts role
      if (user && user?.role?.toLowerCase() === "ts") {
        options.push({
          id: user?._id,
          label: `${user.firstName} ${user.lastName}`,
        });
      }
    }
    return options;
  }, [hierarchyStaff]);

  const allStaffOptions = useMemo(() => {
    let options = [];
    if (staff?.length > 0) {
      // Filter to only show employees with role = "ts" (telesales agents)
      staff
        ?.filter((staffMember) => staffMember?.role?.toLowerCase() === "ts")
        ?.forEach((staffMember) =>
          options.push({
            id: staffMember?._id,
            label: `${staffMember?.firstName} ${staffMember?.lastName}`,
          })
        );
    } else {
      const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY;
      const storedData = localStorage.getItem("user");

      if (storedData) {
        try {
          const bytes = CryptoJS.AES.decrypt(storedData, secretKey);
          user = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        } catch (error) {
          console.error("Failed to decrypt data:", error);
        }
      }

      // Only add user if they have ts role
      if (user && user?.role?.toLowerCase() === "ts") {
        options.push({
          id: user?._id,
          label: `${user.firstName} ${user.lastName}`,
        });
      }
    }
    return options;
  }, [staff]);

  // Staff dropdown source based on role (CEO/Admin see all, others see hierarchy/self)
  const staffOptionsForRole =
    role === "ceo" || role === "admin" || role === "account"
      ? allStaffOptions
      : options.length > 0
      ? options
      : kpiData.staff;

  return (
    <>
      <Main>
        <Search>
          <SubHeading>Reports | TS Activity</SubHeading>
          <SearchSection1>
            <InputSection>
              <h3>Start:</h3>
              <div>
                <input
                  type="date"
                  name="start"
                  id="start"
                  value={kpiData.start}
                  onChange={handleChange}
                  max={maxDate}
                />
              </div>
            </InputSection>
            <InputSection>
              <h3>End:</h3>
              <div>
                <input
                  type="date"
                  name="end"
                  id="end"
                  value={kpiData.end}
                  onChange={handleChange}
                  min={minDate}
                />
              </div>
            </InputSection>
            <InputSection>
              <h3>Project:</h3>
              <div>
                <select
                  name="project"
                  id="project"
                  value={kpiData.project}
                  onChange={handleChange}
                >
                  <option value="">All Projects</option>
                  {project?.map((proj) => (
                    <option key={proj._id} value={proj._id}>
                      {proj.projectName}
                    </option>
                  ))}
                </select>
              </div>
            </InputSection>
          </SearchSection1>
          <SearchSection2>
            <StaffInputFeild>
              <h3>Staff:</h3>
              <Autocomplete
                multiple
                id="size-small-standard-multi"
                size="small"
                options={staffOptionsForRole}
                getOptionLabel={(option) => option.label}
                style={{
                  width: "100%",
                  borderRadius: "5px",
                  minHeight: "2.9em",
                  overflow: "hidden",
                  border: `1px solid ${colors.inputBorderWhite}`,
                }}
                disabled={staffOptionsForRole.length === 0}
                onChange={(e, value) => {
                  setkpiData({ ...kpiData, staff: value });
                }}
                value={kpiData.staff}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    sx={{
                      backgroundColor: `${colors.white}`,
                      paddingTop: "1em",
                    }}
                  />
                )}
              />
            </StaffInputFeild>
            <Button onClick={handleSearch} disabled={isFetching}>
              {isFetching ? <>Searching...</> : <>Search</>}
            </Button>
          </SearchSection2>
        </Search>
      </Main>

      {/* TS KPI Display Component */}
      <TsKpiDisplay
        kpiData={tsKpiData || []}
        isFetching={isFetching}
        startDate={kpiData.start}
        endDate={kpiData.end}
        leadsWorking={leadsWorking}
        overdueLeads={overdueLeads}
        overdueTasksByType={overdueTasksByType}
      />

      {/* Overdue Leads table now rendered inside TsKpiDisplay with DataGrid + pagination */}
    </>
  );
};

const Main = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 30vh;
  padding: 0 0.5rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Search = styled.div`
  background-color: ${colors.inputWhite};
  width: 100%;
  box-shadow: -5px -8px 18px 8px #ffffff, 5px 8px 18px 3px #00000054;
  border-radius: 5px;
  min-height: 19em;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 1em;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.75em;
    min-height: auto;
    gap: 1rem;
  }
`;

const SubHeading = styled.h4`
  height: 30%;
  width: 100%;
  color: ${colors.text};
  font-size: clamp(1.2em, 1.2vw, 1.5em);
  border-bottom: 1px solid ${colors.inputBorderWhite};
  padding-bottom: 1em;
`;

const SearchSection1 = styled.div`
  height: 30%;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const SearchSection2 = styled.div`
  height: 30%;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const InputSection = styled.div`
  width: 100%;
  max-width: 320px;
  min-width: 240px;
  flex: 1 1 auto;

  @media (max-width: 768px) {
    max-width: 100%;
    min-width: 100%;
    width: 100%;
  }

  h3 {
    color: ${colors.black};
    font-size: clamp(1em, 1vw, 2em);
    margin: 1em 0em;

    @media (max-width: 768px) {
      font-size: 0.9rem;
      margin: 0.5em 0em;
    }
  }
  div {
    position: relative;
    border: 1px solid ${colors.inputBorderWhite};
    border-radius: 6px;
    width: 100%;
    padding-right: 1em;
    background-color: ${colors.white};
    select,
    input[type="date"] {
      background-color: ${colors.white};
      width: 100%;
      padding: 1em 0em 1em 1em;
      outline: none;
      border: none;
      font-size: 0.9rem;

      @media (max-width: 768px) {
        padding: 0.75em 0em 0.75em 0.75em;
        font-size: 0.85rem;
      }

      ::placeholder {
        color: ${colors.inputFeild};
      }
    }
  }
`;

const Button = styled.button`
  padding: 1em 2em;
  color: ${colors.white};
  background-color: ${colors.brown};
  border: none;
  border-radius: 5px;
  min-width: 10em;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    padding: 0.875em 1.5em;
    font-size: 0.9rem;
  }

  &:hover:not(:disabled) {
    background-color: #1a6b6b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StaffInputFeild = styled.div`
  width: 85%;
  flex: 1 1 auto;

  @media (max-width: 768px) {
    width: 100%;
  }

  h3 {
    color: ${colors.black};
    font-size: clamp(1em, 1vw, 2em);
    margin: 1em 0em;

    @media (max-width: 768px) {
      font-size: 0.9rem;
      margin: 0.5em 0em;
    }
  }
`;

const TablesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-top: 1rem;
`;

const TableCard = styled.div`
  width: 100%;
  background: ${colors.white};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 1rem;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 6px;
  }

  /* Scrollbar styling for better UX */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.brown};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #1a6b6b;
  }
`;

const TableHeader = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: ${colors.text};
`;

const ResponsiveTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 600px;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    min-width: 500px;
  }

  thead {
    background: ${colors.brown};
    color: ${colors.white};
    th {
      padding: 10px;
      text-align: left;
      font-weight: 600;
      white-space: nowrap;

      @media (max-width: 768px) {
        padding: 8px 6px;
        font-size: 0.75rem;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;
    }
    td {
      padding: 10px;
      color: ${colors.text};

      @media (max-width: 768px) {
        padding: 8px 6px;
        font-size: 0.8rem;
      }
    }
  }
`;

